import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { OFFER_STATUS } from "src/constants";
import type {
  Audience,
  AudienceAttendanceStatusRule,
  FormTemplateSettings,
} from "src/generated/hooks/form-service/requests/types.gen";
import * as GQL from "src/types/graphql";
import { isUserGeneratedKey } from "../ContentTab/V2/autoGenerateKey";
import {
  type AudienceType,
  FormTemplateSettingsType,
  WhatShouldHappenEnum,
} from "./schemas";

// Only one type of attendance rule today, but we will likely have more in the future.
const SUPPORTED_AUDIENCE_RULE_DATA_TYPE = "attendanceStatus" as const;

type AttendanceStatus = AudienceAttendanceStatusRule["value"][number];

const ATTENDANCE_STATUS_CURRENT: AttendanceStatus = "CURRENT";
const ATTENDANCE_STATUS_NEW: AttendanceStatus = "NEW";

function parseAudience(audience: Audience | null): {
  audienceType: AudienceType;
  audienceSchoolIds: string[];
} {
  if (!audience) {
    return { audienceType: "everyone", audienceSchoolIds: [] };
  }

  const ruleGroups = audience.allOf ?? audience.anyOf ?? [];
  const allRules = ruleGroups.flatMap((g) => g.rules ?? []);

  const firstSupportedRule = allRules.find(
    (r): r is AudienceAttendanceStatusRule =>
      (r as { dataType?: string }).dataType ===
      SUPPORTED_AUDIENCE_RULE_DATA_TYPE
  );

  if (!firstSupportedRule) {
    return { audienceType: "everyone", audienceSchoolIds: [] };
  }

  if (firstSupportedRule.value.some((v) => v === ATTENDANCE_STATUS_CURRENT)) {
    return {
      audienceType: "current",
      audienceSchoolIds: firstSupportedRule.schools?.value ?? [],
    };
  }

  if (firstSupportedRule.value.some((v) => v === ATTENDANCE_STATUS_NEW)) {
    return { audienceType: "new", audienceSchoolIds: [] };
  }

  return { audienceType: "everyone", audienceSchoolIds: [] };
}

export const toInitialValues = (
  settings: FormTemplateSettings,
  timezoneName: string
): FormTemplateSettingsType => {
  const openAt = settings.openAt
    ? formatInTimeZone(settings.openAt, timezoneName, "yyyy-MM-dd HH:mm")
    : "";

  const closedAt = settings.closedAt
    ? formatInTimeZone(settings.closedAt, timezoneName, "yyyy-MM-dd HH:mm")
    : "";

  const offerStartAt = settings.offerStartAt
    ? formatInTimeZone(settings.offerStartAt, timezoneName, "yyyy-MM-dd HH:mm")
    : "";

  const reopenAt = settings.reopenAt
    ? formatInTimeZone(settings.reopenAt, timezoneName, "yyyy-MM-dd HH:mm")
    : "";

  const userGeneratedKey = isUserGeneratedKey(settings.key, settings.name);
  const { audienceType, audienceSchoolIds } = parseAudience(settings.audience);

  return {
    active: settings.active,
    locked: settings.locked,
    name: settings.name,
    key: settings.key,
    userGeneratedKey,
    description: settings.description ?? "",
    openAt,
    closedAt,
    offerStartAt,
    reopenAt,
    allowFormDeletionByFamily: settings.allowFormDeletionByFamily,
    lotteryAndOffersEnabled: settings.lotteryOffersEnabled,
    allowDeclineAcceptedOfferEnabled:
      settings.allowDeclineAcceptedOffer !== "none" &&
      settings.allowDeclineAcceptedOffer !== undefined,
    allowDeclineAcceptedOffer: settings.allowDeclineAcceptedOffer,
    schoolSpecificMessagesEnabled: settings.schoolSpecificMessagesEnabled,
    showWaitlistPosition: settings.showWaitlistPosition,
    audienceType,
    audienceSchoolIds,
    allowStaffSchoolSelectionOverride:
      settings.allowStaffSchoolSelectionOverride,
    staffOverrideEligibleLabel:
      settings.staffSchoolSelectionOverrideConfig?.eligibleLabel ?? "",
    staffOverrideIneligibleLabel:
      settings.staffSchoolSelectionOverrideConfig?.ineligibleLabel ?? "",
    staffOverrideReasonType:
      settings.staffSchoolSelectionOverrideConfig?.reason?.type ?? "freeText",
    staffOverrideReasonRequired:
      settings.staffSchoolSelectionOverrideConfig?.reason?.required ?? true,
    staffOverrideReasonOptions:
      settings.staffSchoolSelectionOverrideConfig?.reason?.options ?? [],
  };
};

export const toFormTemplateUpdateBody = (
  values: FormTemplateSettingsType,
  timezoneName: string
) => {
  const {
    active,
    locked,
    allowFormDeletionByFamily,
    allowDeclineAcceptedOfferEnabled,
    allowDeclineAcceptedOffer,
    name,
    key,
    description,
    openAt,
    closedAt,
    offerStartAt,
    lotteryAndOffersEnabled,
    reopenAt,
    schoolSpecificMessagesEnabled,
    showWaitlistPosition,
    audienceType,
    audienceSchoolIds,
    allowStaffSchoolSelectionOverride,
    staffOverrideEligibleLabel,
    staffOverrideIneligibleLabel,
    staffOverrideReasonType,
    staffOverrideReasonRequired,
    staffOverrideReasonOptions,
  } = values;

  const openAtISO = openAt
    ? fromZonedTime(`${openAt}`, timezoneName)?.toISOString()
    : null;

  const closedAtISO = closedAt
    ? fromZonedTime(`${closedAt}`, timezoneName)?.toISOString()
    : null;

  const offerStartAtISO =
    lotteryAndOffersEnabled && offerStartAt
      ? fromZonedTime(`${offerStartAt}`, timezoneName)?.toISOString()
      : null;

  const reopenAtISO = reopenAt
    ? fromZonedTime(`${reopenAt}`, timezoneName)?.toISOString()
    : null;

  return {
    active,
    locked,
    name,
    key,
    description,
    openAt: openAtISO,
    closedAt: closedAtISO,
    offerStartAt: offerStartAtISO,
    allowFormDeletionByFamily,
    allowDeclineAcceptedOffer: allowDeclineAcceptedOfferEnabled
      ? allowDeclineAcceptedOffer ?? "none"
      : "none",
    lotteryOffersEnabled: lotteryAndOffersEnabled,
    reopenAt: reopenAtISO,
    schoolSpecificMessagesEnabled,
    showWaitlistPosition: lotteryAndOffersEnabled && showWaitlistPosition,
    // Persist audience even when locked so toggling the form back on restores the same rules.
    audienceType,
    audienceSchoolIds,
    allowStaffSchoolSelectionOverride,
    // Only sent when enabled; the config is ignored while the toggle is off, so
    // omitting it (undefined) rather than nulling it leaves any prior config
    // untouched and matches the request type (which is not nullable).
    staffSchoolSelectionOverrideConfig: allowStaffSchoolSelectionOverride
      ? {
          eligibleLabel: staffOverrideEligibleLabel.trim() || null,
          ineligibleLabel: staffOverrideIneligibleLabel.trim() || null,
          reason: {
            type: staffOverrideReasonType,
            required: staffOverrideReasonRequired,
            options:
              staffOverrideReasonType === "options"
                ? staffOverrideReasonOptions
                    .map((o) => o.trim())
                    .filter((o) => o !== "")
                : undefined,
          },
        }
      : undefined,
  };
};

export const FormTemplateWhatShouldHappenOptions = [
  {
    label: "Submit",
    value: WhatShouldHappenEnum.Submit,
  },
  {
    label: "Submit and continue to another form",
    value: WhatShouldHappenEnum.SubmitAndContinue,
  },
];

export const FormTemplateRuleTypes = [
  {
    label: "Status or Sub-status",
    value: GQL.rule_type_enum.StatusRule,
  },
];

type Option = {
  label: string;
  key: string;
  value: string;
  disabled?: boolean;
  isSubStatus?: boolean;
};

export const FormTemplateStatusRuleOptions: Option[] = [
  {
    label: OFFER_STATUS[GQL.offer_status_enum.Accepted].label,
    value: GQL.offer_status_enum.Accepted,
    key: GQL.offer_status_enum.Accepted,
    isSubStatus: true,
  },
];
